import {Dayjs} from "dayjs";

import {
	CompartmentObject,
	ObjectV6,
	PrebookObjectV4,
	PrivateVariationAllocationObject,
	Relation
} from "../../api/Ticketshop";
import {DATA_STORE_KEY_PASSENGERS, dataStore} from "../../global/DataStore";
import {formatIsoDate, getDateYearsAgo, now} from "../../utils/date";
import {SelectedUser} from "./nj-passangers";
import {PassangerType} from "./PassangerType";
import {Gender} from "../../utils/genderUtilities";

export const maxPersons: number = 6;
export const maxBikes: number = 5;
export const maxObjects: number = 10;

export function isAdult(passengerType: PassangerType): boolean {
	return passengerType === PassangerType.Adult;
}

export function isChild(passengerType: PassangerType): boolean {
	return passengerType === PassangerType.TenToFourteenYearOldChild || passengerType === PassangerType.Child || passengerType === PassangerType.SmallChildSeat || passengerType === PassangerType.SmallChild;
}

export function isHumanPassangerType(njPassangerType: PassangerType): boolean {
    if (njPassangerType === PassangerType.Dog) { return false; }
    if (njPassangerType === PassangerType.Bike) { return false; }
    if (njPassangerType === PassangerType.Item) { return false; }
    if (njPassangerType === PassangerType.Motorbike) { return false; }
    if (njPassangerType === PassangerType.Car) { return false; }

    return true;
}

export function isHumanPassanger(njUser: SelectedUser): boolean {
    return isHumanPassangerType(njUser.njPassangerType);
}

export function getTicketshopPassangerTypeFromNjUserPassangerType(njPassangerType: PassangerType): ObjectV6['type'] {
    if (njPassangerType === PassangerType.Adult) { return 'person'; }
    if (njPassangerType === PassangerType.Child) { return 'person'; }
    if (njPassangerType === PassangerType.SmallChild) { return 'person'; }
    if (njPassangerType === PassangerType.SmallChildSeat) { return 'person'; }
	if (njPassangerType === PassangerType.TenToFourteenYearOldChild) { return "person"; }

    return njPassangerType;
}

export function getTicketShopGenderFromNjGender(gender: Gender): "male" | "female" {
	switch (gender) {
		case "female":
			return "female";
		case "diverse":
		case "male":
			return "male";
	}
}

export function getDefaultPassanger(
    passangerType: PassangerType = PassangerType.Adult,
    gender: Gender = 'female',
    birthDate?: string,
): SelectedUser[] {
    const randomId: string = createRandomName();
    const defaultUser: SelectedUser = {
        njUserName: passangerType + '-' + randomId,
        njPassangerType: passangerType,
        njCards: [],
        njChallengedOptions: {
            wheelchair: false,
            attendant: false,
            dog: false,
        },

        type: getTicketshopPassangerTypeFromNjUserPassangerType(passangerType),
    };

    if (isHumanPassangerType(passangerType)) {
        defaultUser.gender = gender;
        defaultUser.birthDate = birthDate ? birthDate : getDateYearsAgo(30);
        defaultUser.cards = [];
    }

    return [defaultUser];
}

export function createRandomName(length: number = 5) {
    var result           = '';
    var characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var charactersLength = characters.length;
    for ( var i = 0; i < length; i++ ) {
      result += characters.charAt(Math.floor(Math.random() *
        charactersLength));
   }
   return result;
}

export function getBirthdateForPassangertype(passangerType: PassangerType): string {
    let yearsAgo = 30;

    if (!isHumanPassangerType(passangerType)) {
		return;
	}

	if (passangerType == PassangerType.TenToFourteenYearOldChild) {
		yearsAgo = 10;
	}
    if (passangerType == PassangerType.Child) {
		yearsAgo = 8;
	}
    else if (passangerType == PassangerType.SmallChild) {
		yearsAgo = 0;
	}
    else if (passangerType == PassangerType.SmallChildSeat) {
		yearsAgo = 8;
	}

    return getDateYearsAgo(yearsAgo);
}

export function getMinBirthDates(baseDate: Dayjs): { [type: string]: string } {
	baseDate = baseDate.add(1, 'day');
    const smallChildDate = getDateYearsAgo(6, baseDate);
    return {
        [PassangerType.Adult]: '1900-01-01',
		[PassangerType.TenToFourteenYearOldChild]: getDateYearsAgo(15, baseDate),
		[PassangerType.Child]: getDateYearsAgo(10, baseDate),
        [PassangerType.SmallChildSeat]: smallChildDate,
        [PassangerType.SmallChild]: smallChildDate,
    };
}

export function getMaxBirthDates(baseDate: Dayjs): { [type: string]: string } {
    const smallChildDate = formatIsoDate(now())
    return {
        [PassangerType.Adult]: getDateYearsAgo(15, baseDate),
		[PassangerType.TenToFourteenYearOldChild]: getDateYearsAgo(10, baseDate),
		[PassangerType.Child]: getDateYearsAgo(6, baseDate),
        [PassangerType.SmallChildSeat]: smallChildDate,
        [PassangerType.SmallChild]: smallChildDate,
    };
}

export function getPassangerTypeByDate(birthDate: Dayjs, travelDate: Dayjs): PassangerType {
   const maxBirthDates = getMaxBirthDates(travelDate)
   const minBirthDates = getMinBirthDates(travelDate)

   const birthDateString = formatIsoDate(birthDate);

   if (birthDateString >= minBirthDates[PassangerType.Adult] && birthDateString <= maxBirthDates[PassangerType.Adult]) {
       return PassangerType.Adult;
   }
   if (birthDateString >= minBirthDates[PassangerType.TenToFourteenYearOldChild] && birthDateString <= maxBirthDates[PassangerType.TenToFourteenYearOldChild]) {
       return PassangerType.TenToFourteenYearOldChild;
   }
   if (birthDateString >= minBirthDates[PassangerType.Child] && birthDateString <= maxBirthDates[PassangerType.Child]) {
       return PassangerType.Child;
   }
   if (birthDateString >= minBirthDates[PassangerType.SmallChild] && birthDateString <= maxBirthDates[PassangerType.SmallChild]) {
       return PassangerType.SmallChild;
   }
   if (birthDateString >= minBirthDates[PassangerType.SmallChildSeat] && birthDateString <= maxBirthDates[PassangerType.SmallChildSeat]) {
       return PassangerType.SmallChildSeat;
   }

   return null;

}

export function setPassangersIntoStore(passangers: any) {
    dataStore().setSessionData(DATA_STORE_KEY_PASSENGERS, passangers);
}

export function getPassangersFromStore(): SelectedUser[] {
    return dataStore().getSessionData(DATA_STORE_KEY_PASSENGERS, null);
}

export function getPassangersFilteredFromStore() {
    const passangers = dataStore().getSessionData(DATA_STORE_KEY_PASSENGERS, null);
    if (!passangers) return null;

    return passangers.filter(passanger => isHumanPassangerType(passanger.type));
}

export function passengersAreValid(): boolean {
    const passengers = getPassangersFromStore();
    return !!passengers?.find(p => [PassangerType.Adult, PassangerType.TenToFourteenYearOldChild, PassangerType.Child].includes(p.njPassangerType));
}

export function countObjects(njUsers: SelectedUser[]): { objects: number, persons: number, bikes: number } {
    let countObjects: number = 0;
    let countPersons: number = 0;
    let countBikes: number = 0;

    for (const njUser of njUsers) {
        countObjects++;

        if (njUser.type === 'person') {
            countPersons++;

            if (njUser.njChallengedOptions?.attendant) {
                countObjects++;
                countPersons++;
            }

            if (njUser.njChallengedOptions?.dog) {
                countObjects++;
            }

            if (njUser.njChallengedOptions?.wheelchair) {
                countObjects++;
            }
        } else if (njUser.type === 'bike') {
            countBikes++;
        }
    }

    return {
        objects: countObjects,
        persons: countPersons,
        bikes: countBikes,
    };
}

export function canAddPassangerObject(njUsers: SelectedUser[]): boolean {
    const { objects } = countObjects(njUsers);

    if (objects >= maxObjects) return false;

    return true;
}

export function canAddPassangerBike(njUsers: SelectedUser[]): boolean {
    const { persons, bikes } = countObjects(njUsers);

    if (!canAddPassangerObject(njUsers)) return false;
    if (persons <= bikes) return false;
    if (bikes >= maxBikes) return false; // Should not be necessary because of maximum 10 objects (conditions above), but leave it for completeness

    return true;
}

export function canAddPassangerPerson(njUsers: SelectedUser[]): boolean {
    const { persons } = countObjects(njUsers);

    if (!canAddPassangerObject(njUsers)) return false;
    if (persons >= maxPersons) return false;

    return true;
}

export function removeBikes(njUsers: SelectedUser[]) {
    let i: number = njUsers.length ?? 0;

    while (i--) {
        if (njUsers[i].type === PassangerType.Bike) {
            njUsers.splice(i, 1);
        }
    }

    return njUsers;
}

export function canUseFemaleCompartment(njUsers: SelectedUser[]): boolean {
    for (const njUser of njUsers) {
        if (!isHumanPassanger(njUser)) { continue; }
        if (njUser.gender === 'female') { continue; }
        if (njUser.njPassangerType === PassangerType.SmallChild) { continue; }
        if (njUser.njPassangerType === PassangerType.SmallChildSeat) { continue; }

        if (njUser.gender === "male" || njUser.gender === "diverse") { return false; }
    }

    return true;
}

export function canUseSharedSleeperCompartments(njUsers: SelectedUser[]): boolean {
	for (const passenger of njUsers) {
		if (isHumanPassanger(passenger) && passenger.njPassangerType === PassangerType.Adult && passenger.gender === "diverse") {
			return false;
		}
	}
	return true;
}

/**
 * Remove bike if there are more bikes than passangers (including challenged attendants)
 **/
export function removeBikeIfMoreBikesThanPassangers(njUsers: SelectedUser[]) {
    const { persons, bikes } = countObjects(njUsers);

    if (bikes > persons) {
      const firstBike: number = njUsers.findIndex((passanger => passanger.njPassangerType === PassangerType.Bike));
      if (firstBike > -1) njUsers.splice(firstBike, 1);
    }
}

export function transformNjPassangerDataToTsPrebookObjectsAndRelations(njUsers?: SelectedUser[]): { objects: PrebookObjectV4[], relations: Relation[] } {
    if (!njUsers) {
        njUsers = getPassangersFromStore();
    }
    if (!njUsers) {
        return {objects: [], relations: []};
    }

    const objects: PrebookObjectV4[] = [];
    const relations: Relation[] = [];

    for (const user of njUsers) {
        const njUser: SelectedUser = {...user};
        const njChallengedOptions = user.njChallengedOptions;

        // Change type to "challenged" if a card is used
        for (const njCard of user.njCards) {
            if (njCard.isChallenged) {
                user.type = 'challenged';
            }
        }

        // Remove our custom properties
        for (const prop in njUser) {
            if (!['njUserName', 'njPassangerType', 'njBirthDate', 'njCards', 'njChallengedOptions'].includes(prop)) continue;
            delete njUser[prop];
        }

        objects.push(njUser);
        const i = objects.length - 1;

        // Check challenged options and apply them
        if (njChallengedOptions) {
            if (njChallengedOptions.attendant) {
                objects[i].type = 'challenged';

                objects.push({
                    type: 'person',
                    gender: objects[i].gender,
                    firstName: objects[i].firstName ? `${objects[i].firstName} (Begleitperson)` : 'Begleitperson',
                    lastName: objects[i].lastName,
                    birthDate: getDateYearsAgo(30),
                });

                relations.push({
                    lhs: objects.length - 1,
                    relationType: 'attendant_of',
                    rhs: i,
                });
            }

            if (njChallengedOptions.dog) {
                objects[i].type = 'challenged';

                objects.push({
                    type: 'dog',
                });

                relations.push({
                    lhs: objects.length - 1,
                    relationType: 'attendant_of',
                    rhs: i,
                });
            }

            // Override type event if it is "challenged", beause "wheelchair" is important to get offers
            if (njChallengedOptions.wheelchair) {
                objects[i].type = 'wheelchair';
            }
        }
    }

    return { objects, relations };
}

export function combineAllocationsOrCompartmentObjectsWihPassangers(allocationsOrCompartmentObjects: (PrivateVariationAllocationObject|CompartmentObject)[], passangers: SelectedUser[]) {
    const allocationsOrCompartmentsWithPassangers: {
        allocationOrCompartment: PrivateVariationAllocationObject|CompartmentObject,
        passanger?: SelectedUser
    }[] = [];

    let allocationOrCompartmentObjectCount = 0;

    for (const passanger of passangers) {
        allocationsOrCompartmentsWithPassangers.push({
            allocationOrCompartment: allocationsOrCompartmentObjects[allocationOrCompartmentObjectCount],
            passanger: passanger
        });

        if (passanger.njChallengedOptions?.attendant) {
            allocationOrCompartmentObjectCount++;

            allocationsOrCompartmentsWithPassangers.push({
                allocationOrCompartment: allocationsOrCompartmentObjects[allocationOrCompartmentObjectCount],
                passanger: null
            });
        }

        allocationOrCompartmentObjectCount++;
    }

    return allocationsOrCompartmentsWithPassangers;
}
